import React, { useState, useEffect, useCallback } from 'react';
import { X, Plus, Minus, Search, CreditCard, DollarSign, Smartphone, FileText, Settings } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { dataService, MenuItem, OrderItem, Order } from '../../services/dataService';
import AddOnsSelectionModal from './AddOnsSelectionModal';
import { createCheckoutIdempotencyKey } from '../../utils/checkoutIdempotency';

interface AddItemToOrderModalProps {
  order: Order;
  currentTab: 'requested' | 'preparing' | 'completed';
  onClose: () => void;
  onSuccess: () => void;
  posSimplified?: boolean;
  onOptimisticItemsAdd?: (
    orderId: string,
    items: Array<{
      menuItem: any;
      quantity: number;
      notes: string;
    }>,
    currentTab: 'requested' | 'preparing' | 'completed',
    paymentMethod?: 'cash' | 'card' | 'digital'
  ) => Promise<void>;
}

const AddItemToOrderModal: React.FC<AddItemToOrderModalProps> = ({ 
  order, 
  currentTab, 
  onClose, 
  onSuccess,
  onOptimisticItemsAdd,
  posSimplified = false
}) => {
  const { tenantData } = useAuth();
  const [loading, setLoading] = useState(false);
  const [addingItemId, setAddingItemId] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  
  // Menu filtering and search
  const [menuSearchTerm, setMenuSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  
  // Payment method state
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<'cash' | 'card' | 'digital'>('digital');
  
  const [selectedItems, setSelectedItems] = useState<{
    menuItem: MenuItem;
    quantity: number;
    notes: string;
    addOnSelections?: Array<{
      categoryId: string;
      categoryName: string;
      selectedOptions: Array<{
        optionId: string;
        optionName: string;
        price: number;
      }>;
    }>;
  }[]>([]);

  // Add-ons modal state
  const [showAddOnsModal, setShowAddOnsModal] = useState(false);
  const [pendingMenuItem, setPendingMenuItem] = useState<MenuItem | null>(null);

  const loadMenuItems = useCallback(async () => {
    try {
      const items = await dataService.getAvailableMenuItems(tenantData!.id);
      setMenuItems(items);
    } catch (error) {
      console.error('Error loading menu items:', error);
    }
  }, [tenantData]);

  useEffect(() => {
    if (tenantData?.id) {
      loadMenuItems();
    }
  }, [tenantData?.id, loadMenuItems]);

  // Get unique categories from menu items
  const getMenuCategories = useCallback(() => {
    const categories = Array.from(new Set(menuItems.map(item => item.category))).sort();
    return ['all', ...categories];
  }, [menuItems]);

  // Filter menu items based on search and category
  const getFilteredMenuItems = useCallback(() => {
    return menuItems.filter(item => {
      const matchesSearch = menuSearchTerm === '' || 
        item.name.toLowerCase().includes(menuSearchTerm.toLowerCase()) ||
        (item.description && item.description.toLowerCase().includes(menuSearchTerm.toLowerCase()));
      
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      
      return matchesSearch && matchesCategory;
    });
  }, [menuItems, menuSearchTerm, selectedCategory]);

  const addMenuItem = async (menuItem: MenuItem) => {
    // Prevent adding if already processing this item
    if (addingItemId === menuItem.id) return;
    
    setAddingItemId(menuItem.id!);
    
    try {
      // Check if this menu item has add-ons configured
      if (tenantData?.id) {
        const addOnsConfig = await dataService.getMenuItemAddOns(tenantData.id, menuItem.id!);
        
        if (addOnsConfig && addOnsConfig.addOnCategories && addOnsConfig.addOnCategories.length > 0) {
          // Show add-ons selection modal
          setPendingMenuItem(menuItem);
          setShowAddOnsModal(true);
          return;
        }
      }
      
      // No add-ons, add item directly
      addItemToSelection(menuItem);
    } catch (error) {
      console.error('Error checking add-ons:', error);
      // Fallback to adding item directly
      addItemToSelection(menuItem);
    } finally {
      // Clear loading state after a short delay to prevent rapid clicking
      setTimeout(() => {
        setAddingItemId(null);
      }, 300);
    }
  };

  const addItemToSelection = (menuItem: MenuItem, addOnSelections?: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>) => {
    // Check if item with same add-ons already exists
    const existingIndex = selectedItems.findIndex(item => {
      // First check if it's the same menu item
      if (item.menuItem.id !== menuItem.id) return false;
      
      // If no add-ons on either, treat as same item
      if (!item.addOnSelections && !addOnSelections) return true;
      
      // If one has add-ons and the other doesn't, treat as different items
      if (!item.addOnSelections || !addOnSelections) return false;
      
      // If both have add-ons, check if they're identical
      if (item.addOnSelections.length !== addOnSelections.length) return false;
      
      // Check if all categories and options match exactly
      return item.addOnSelections.every((itemCategory, index) => {
        const newCategory = addOnSelections[index];
        if (itemCategory.categoryId !== newCategory.categoryId) return false;
        if (itemCategory.selectedOptions.length !== newCategory.selectedOptions.length) return false;
        
        return itemCategory.selectedOptions.every((itemOption, optIndex) => {
          const newOption = newCategory.selectedOptions[optIndex];
          return itemOption.optionId === newOption.optionId;
        });
      });
    });
    
    if (existingIndex >= 0) {
      // Increase quantity if item with identical add-ons already exists
      setSelectedItems(prev => prev.map((item, index) => 
        index === existingIndex 
          ? { ...item, quantity: item.quantity + 1 }
          : item
      ));
    } else {
      // Add new item (either different menu item or same item with different add-ons)
      setSelectedItems(prev => [...prev, {
        menuItem,
        quantity: 1,
        notes: '',
        addOnSelections
      }]);
    }
  };

  const handleAddOnsConfirm = (selectedAddOns: Array<{
    categoryId: string;
    categoryName: string;
    selectedOptions: Array<{
      optionId: string;
      optionName: string;
      price: number;
    }>;
  }>) => {
    if (pendingMenuItem) {
      addItemToSelection(pendingMenuItem, selectedAddOns);
      setPendingMenuItem(null);
      setShowAddOnsModal(false);
    }
  };

  const handleAddOnsCancel = () => {
    setPendingMenuItem(null);
    setShowAddOnsModal(false);
    setAddingItemId(null);
  };

  const updateItemQuantity = (index: number, quantity: number) => {
    if (quantity <= 0) {
      removeItem(index);
      return;
    }
    
    setSelectedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, quantity } : item
    ));
  };

  const addSameItemWithAddOns = async (index: number) => {
    const existingItem = selectedItems[index];
    if (!existingItem || !tenantData?.id) return;
    
    try {
      // Check if this menu item has add-ons configured
      const addOnsConfig = await dataService.getMenuItemAddOns(tenantData.id, existingItem.menuItem.id!);
      
      if (addOnsConfig && addOnsConfig.addOnCategories && addOnsConfig.addOnCategories.length > 0) {
        // Show add-ons selection modal for the same item
        setPendingMenuItem(existingItem.menuItem);
        setShowAddOnsModal(true);
      } else {
        // No add-ons, just increase quantity
        setSelectedItems(prev => prev.map((item, i) => 
          i === index ? { ...item, quantity: item.quantity + 1 } : item
        ));
      }
    } catch (error) {
      console.error('Error checking add-ons:', error);
      // Fallback to increasing quantity
      setSelectedItems(prev => prev.map((item, i) => 
        i === index ? { ...item, quantity: item.quantity + 1 } : item
      ));
    }
  };

  const updateItemNotes = (index: number, notes: string) => {
    setSelectedItems(prev => prev.map((item, i) => 
      i === index ? { ...item, notes } : item
    ));
  };

  const removeItem = (index: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const calculateTotal = () => {
    const subtotal = selectedItems.reduce((sum, item) => {
      let itemTotal = item.menuItem.price * item.quantity;
      
      // Add add-ons pricing
      if (item.addOnSelections) {
        item.addOnSelections.forEach(category => {
          category.selectedOptions.forEach(option => {
            itemTotal += option.price * item.quantity;
          });
        });
      }
      
      return sum + itemTotal;
    }, 0);
    
    return {
      subtotal,
      tax: 0, // No tax
      total: subtotal // Total equals subtotal
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedItems.length === 0) {
      setError('Please add at least one item');
      return;
    }

    // Use optimistic UI if available, otherwise fallback to original method
    if (onOptimisticItemsAdd) {
      try {
        setError('');
        
        // Close modal FIRST, then start optimistic update
        onSuccess();
        onClose();
        
        // Call optimistic function in background (don't await!)
        onOptimisticItemsAdd(order.id!, selectedItems.map(item => ({
          menuItem: item.menuItem,
          quantity: item.quantity,
          notes: item.notes,
          addOnSelections: item.addOnSelections
        })), currentTab, selectedPaymentMethod).catch(error => {
          console.error('Error in optimistic items addition:', error);
          // The optimistic function handles its own error display
        });
      } catch (error) {
        console.error('Error setting up optimistic items addition:', error);
        setError('Failed to add items to order. Please try again.');
      }
    } else {
      // Fallback to original method
      try {
        setLoading(true);
        setError('');

        // Add each selected item to the existing order
        for (const selectedItem of selectedItems) {
          let totalPrice = selectedItem.menuItem.price * selectedItem.quantity;
          
          // Calculate add-ons pricing
          if (selectedItem.addOnSelections) {
            selectedItem.addOnSelections.forEach(category => {
              category.selectedOptions.forEach(option => {
                totalPrice += option.price * selectedItem.quantity;
              });
            });
          }
          
          // Create a unique identifier that includes add-ons for proper tracking
          const addOnsSignature = selectedItem.addOnSelections 
            ? selectedItem.addOnSelections.map(cat => 
                `${cat.categoryId}:${cat.selectedOptions.map(opt => opt.optionId).sort().join(',')}`
              ).sort().join('|')
            : 'no-addons';
          
          const orderItem: OrderItem = {
            id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            menuItemId: selectedItem.menuItem.id!,
            menuItemName: selectedItem.menuItem.name,
            quantity: selectedItem.quantity,
            unitPrice: selectedItem.menuItem.price,
            totalPrice: totalPrice,
            status: currentTab, // Set status based on current tab
            addOnSelections: selectedItem.addOnSelections, // Include add-ons data
            addOnsSignature: addOnsSignature // Include add-ons signature for tracking
          };

          // Only add notes if they exist and are not empty
          if (selectedItem.notes && selectedItem.notes.trim()) {
            orderItem.notes = selectedItem.notes.trim();
          }

          await dataService.addItemToOrder(tenantData!.id, order.id!, orderItem);
        }

        // Process payment if a payment method is selected
        if (selectedPaymentMethod && selectedPaymentMethod !== 'digital') {
          const { total } = calculateTotal();
          await dataService.addPaymentToOrder(
            tenantData!.id,
            order.id!,
            total,
            selectedPaymentMethod,
            undefined,
            { idempotencyKey: createCheckoutIdempotencyKey('payment') }
          );
        }

        // Close modal after successful addition
        onSuccess();
        onClose();
      } catch (error) {
        console.error('Error adding items to order:', error);
        setError('Failed to add items to order. Please try again.');
        setLoading(false);
      }
    }
  };

  const { subtotal, tax, total } = calculateTotal();

  const paymentMethods = [
    { value: 'card', label: 'Credit Card', icon: CreditCard, color: 'bg-blue-500' },
    { value: 'digital', label: 'Paylater', icon: Smartphone, color: 'bg-gray-500' },
    { value: 'cash', label: 'Cash Payout', icon: DollarSign, color: 'bg-green-500' }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 md:p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-7xl h-[95vh] md:h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Add Items to Order</h2>
            {!posSimplified && (
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                currentTab === 'requested' ? 'bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400' :
                currentTab === 'preparing' ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-400' :
                'bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400'
              }`}>
                {currentTab.charAt(0).toUpperCase() + currentTab.slice(1)} Tab
              </span>
            )}
          </div>
          
          {/* Search Bar in Header */}
          <div className="flex-1 max-w-md mx-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500" />
              <input
                type="text"
                value={menuSearchTerm}
                onChange={(e) => setMenuSearchTerm(e.target.value)}
                className="w-full pl-9 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Search Your Menu Here"
              />
            </div>
          </div>
          
          {/* Customer Info */}
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2 bg-white dark:bg-gray-600 px-3 py-2 rounded-lg border border-gray-200 dark:border-gray-500">
              <div>
                <p className="font-medium text-gray-900 dark:text-white text-sm">{order.customer}</p>
                {order.customerPhone && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">{order.customerPhone}</p>
                )}
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left Panel - Menu */}
          <div className="flex-1 flex flex-col overflow-hidden order-2 md:order-1">
            {/* Category Tabs */}
            <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
              <div className="flex space-x-2 overflow-x-auto">
                {getMenuCategories().map(category => (
                  <button
                    key={category}
                    onClick={() => setSelectedCategory(category)}
                    className={`flex-shrink-0 px-4 py-2 rounded-lg font-medium text-xs transition-all duration-200 ${
                      selectedCategory === category
                        ? 'bg-blue-500 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-500'
                    }`}
                  >
                    {category === 'all' ? 'All Menu' : category}
                    <span className="ml-1 text-xs opacity-75">
                      ({category === 'all' 
                        ? menuItems.length 
                        : menuItems.filter(item => item.category === category).length
                      })
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Menu Grid */}
            <div className="flex-1 overflow-y-auto p-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                {getFilteredMenuItems().map((item) => {
                  const selectedItem = selectedItems.find(si => si.menuItem.id === item.id);
                  const quantity = selectedItem?.quantity || 0;
                  
                  return (
                    <div key={item.id} className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-3 hover:shadow-md transition-all duration-200">
                      {/* Horizontal Layout */}
                      <div className="flex items-center space-x-3">
                        {/* Item Image */}
                        <div className="w-16 h-16 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                          <span className="text-lg">🍽️</span>
                          {item.image && (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="absolute inset-0 w-full h-full object-cover"
                              loading="lazy"
                              onError={(e) => {
                                (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                              }}
                            />
                          )}
                        </div>
                        
                        {/* Item Info */}
                        <div className="flex-1 min-w-0">
                          <h3 className="font-semibold text-gray-900 dark:text-white text-sm line-clamp-1">{item.name}</h3>
                          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1 mb-1">{item.description || 'Delicious item'}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-sm font-bold text-gray-900 dark:text-white">₹{item.price}</span>
                            <span className="text-xs text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1 py-0.5 rounded">{item.category}</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Quantity Controls */}
                      <div className="mt-2">
                        {quantity > 0 ? (
                          <div className="flex items-center justify-between bg-blue-50 dark:bg-blue-900/30 rounded-lg p-2">
                            <button
                              onClick={() => {
                                if (selectedItem) {
                                  const index = selectedItems.findIndex(si => si.menuItem.id === item.id);
                                  updateItemQuantity(index, quantity - 1);
                                }
                              }}
                              className="w-6 h-6 bg-white dark:bg-gray-600 border border-blue-200 dark:border-blue-700 rounded-md flex items-center justify-center text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/50"
                            >
                              <Minus className="w-3 h-3" />
                            </button>
                            <span className="font-bold text-blue-600 dark:text-blue-400 text-sm px-2">{quantity}</span>
                            <button
                              onClick={() => {
                                if (selectedItem) {
                                  const index = selectedItems.findIndex(si => si.menuItem.id === item.id);
                                  updateItemQuantity(index, quantity + 1);
                                }
                              }}
                              className="w-6 h-6 bg-blue-600 rounded-md flex items-center justify-center text-white hover:bg-blue-700"
                            >
                              <Plus className="w-3 h-3" />
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => addMenuItem(item)}
                            disabled={addingItemId === item.id}
                            className="w-full bg-blue-600 text-white py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center text-sm"
                          >
                            {addingItemId === item.id ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <Plus className="w-3 h-3" />
                            )}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
              
              {getFilteredMenuItems().length === 0 && (
                <div className="text-center py-8">
                  <Search className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-2" />
                  <p className="text-gray-500 dark:text-gray-400">No items found</p>
                  <p className="text-gray-400 dark:text-gray-500 text-sm">Try adjusting your search or category filter</p>
                </div>
              )}
            </div>
          </div>

          {/* Right Panel - Cart */}
          <div className="w-full md:w-80 bg-gray-50 dark:bg-gray-700 border-t md:border-l border-gray-200 dark:border-gray-600 flex flex-col order-1 md:order-2">
            {/* Cart Header */}
            <div className="p-3 border-b border-gray-200 dark:border-gray-600">
              <h3 className="text-base font-bold text-gray-900 dark:text-white">Add Items</h3>
            </div>

            {/* Selected Items */}
            <div className="flex-1 overflow-y-auto p-3 space-y-2">
              {selectedItems.length > 0 ? (
                selectedItems.map((item, index) => (
                  <div key={index} className="bg-white dark:bg-gray-600 rounded-lg p-2 border border-gray-200 dark:border-gray-500">
                    <div className="flex items-start space-x-2">
                      {/* Item Image */}
                      <div className="w-8 h-8 bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-md overflow-hidden flex items-center justify-center flex-shrink-0 relative">
                        <span className="text-xs">🍽️</span>
                        {item.menuItem.image && (
                          <img
                            src={item.menuItem.image}
                            alt={item.menuItem.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => {
                              (e.currentTarget as HTMLImageElement).style.visibility = 'hidden';
                            }}
                          />
                        )}
                      </div>
                      
                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-1">
                          <h4 className="font-medium text-gray-900 dark:text-white text-xs line-clamp-1">{item.menuItem.name}</h4>
                          <span className="font-bold text-xs text-gray-900 dark:text-white">
                            ₹{(() => {
                              let itemTotal = item.menuItem.price * item.quantity;
                              if (item.addOnSelections) {
                                item.addOnSelections.forEach(category => {
                                  category.selectedOptions.forEach(option => {
                                    itemTotal += option.price * item.quantity;
                                  });
                                });
                              }
                              return itemTotal.toFixed(1);
                            })()}
                          </span>
                        </div>
                        
                        {/* Add-ons Display */}
                        {item.addOnSelections && item.addOnSelections.length > 0 && (
                          <div className="mb-1">
                            {item.addOnSelections.map((category, catIndex) => (
                              <div key={catIndex} className="text-xs text-gray-600 dark:text-gray-400">
                                <span className="font-medium">{category.categoryName}:</span>
                                {category.selectedOptions.map((option, optIndex) => (
                                  <span key={optIndex} className="ml-1">
                                    {option.optionName}
                                    {option.price > 0 && <span className="text-gray-500">(+₹{option.price})</span>}
                                    {optIndex < category.selectedOptions.length - 1 ? ', ' : ''}
                                  </span>
                                ))}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {/* Quantity Controls */}
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-1">
                            <button
                              onClick={() => updateItemQuantity(index, item.quantity - 1)}
                              className="w-4 h-4 bg-gray-200 dark:bg-gray-500 rounded-full flex items-center justify-center text-gray-600 dark:text-gray-300"
                            >
                              <Minus className="w-2 h-2" />
                            </button>
                            <span className="font-medium text-xs px-1 min-w-[16px] text-center text-gray-900 dark:text-white">{item.quantity}</span>
                            <button
                              onClick={() => addSameItemWithAddOns(index)}
                              className="w-4 h-4 bg-blue-600 text-white rounded-full flex items-center justify-center hover:bg-blue-700 transition-colors"
                              title="Add same item (may show add-ons selection)"
                            >
                              <Plus className="w-2 h-2" />
                            </button>
                          </div>
                          
                          <div className="flex items-center space-x-1">
                            <span className="text-xs text-gray-500 dark:text-gray-400">₹{item.menuItem.price}</span>
                            <button
                              onClick={() => removeItem(index)}
                              className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300"
                            >
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        </div>
                        
                        {/* Notes */}
                        {(item.notes || index === selectedItems.length - 1) && (
                          <input
                            type="text"
                            value={item.notes}
                            onChange={(e) => updateItemNotes(index, e.target.value)}
                            className="w-full text-xs border border-gray-200 dark:border-gray-500 rounded px-1 py-0.5 mt-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
                            placeholder="Notes..."
                          />
                        )}
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-4">
                  <div className="w-8 h-8 bg-gray-200 dark:bg-gray-600 rounded-full mx-auto mb-2 flex items-center justify-center">
                    <FileText className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                  </div>
                  <p className="text-gray-500 dark:text-gray-400 text-xs">No items selected</p>
                  <p className="text-gray-400 dark:text-gray-500 text-xs">Add items from the menu</p>
                </div>
              )}
            </div>

            {/* Payment Summary & Actions */}
            {selectedItems.length > 0 && (
              <div className="p-3 border-t border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-600">
                {/* Payment Summary */}
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-300 text-xs">Sub Total</span>
                    <span className="font-medium text-xs text-gray-900 dark:text-white">₹{subtotal.toFixed(1)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-bold border-t border-gray-200 dark:border-gray-500 pt-1">
                    <span className="text-gray-900 dark:text-white">Total to Add</span>
                    <span className="text-gray-900 dark:text-white">₹{total.toFixed(1)}</span>
                  </div>
                </div>

                {/* Payment Methods */}
                <div className="grid grid-cols-3 gap-1 mb-3">
                  {paymentMethods.map((method) => (
                    <button
                      key={method.value}
                      onClick={() => setSelectedPaymentMethod(method.value as any)}
                      className={`p-1 rounded border transition-all duration-200 ${
                        selectedPaymentMethod === method.value
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                          : 'border-gray-200 dark:border-gray-500 hover:border-gray-300 dark:hover:border-gray-400'
                      }`}
                    >
                      <div className={`w-5 h-5 ${method.color} rounded mx-auto mb-0.5 flex items-center justify-center`}>
                        <method.icon className="w-2 h-2 text-white" />
                      </div>
                      <p className="text-xs font-medium text-center leading-tight text-gray-900 dark:text-white">{method.label}</p>
                    </button>
                  ))}
                </div>

                {/* Add Items Button */}
                <button
                  onClick={handleSubmit}
                  disabled={loading || selectedItems.length === 0}
                  className="w-full bg-blue-600 text-white py-2 rounded-lg font-bold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 text-sm"
                >
                  {loading ? 'Adding...' : selectedPaymentMethod && selectedPaymentMethod !== 'digital' 
                    ? `Add Items & Pay ₹${total.toFixed(1)} (${selectedPaymentMethod})` 
                    : 'Add Items to Order'
                  }
                </button>

                {error && (
                  <p className="text-red-600 dark:text-red-400 text-xs mt-1 text-center">{error}</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Add-Ons Selection Modal */}
        {showAddOnsModal && pendingMenuItem && (
          <AddOnsSelectionModal
            menuItem={pendingMenuItem}
            onClose={handleAddOnsCancel}
            onConfirm={handleAddOnsConfirm}
          />
        )}
        

      </div>
    </div>
  );
};

export default AddItemToOrderModal; 